import {
  AssetRoutineState,
  useFindByQuery,
  useGrippContext,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { RoutineModules } from '../routine';
import { SubHeader, SubHeaderProps } from './headerTypes';

interface SortedRoutineState {
  state?: AssetRoutineState<any>;
  config: any;
  order?: number;
  type: any;
  routines?: WorkspaceRoutine[];
}

export const RoutineSubHeader: SubHeader = (props: SubHeaderProps) => {
  const { logger } = useGrippContext();
  const { workspaceAsset } = props;
  const [sorted, setSorted] = useState<SortedRoutineState[] | undefined>();
  const { data } = useFindByQuery({
    modelName: 'WorkspaceRoutineVersion',
    query: GET_ROUTINE_VERSIONS,
    variables: {
      filter: {
        id: {
          in: [
            ...(workspaceAsset.routineState?.map((x) => x.workspaceRoutineVersionId) || []),
            ...(workspaceAsset.routines?.map((r) => r.currentVersion?.id).filter(Boolean) || [])
          ],
        },
      },
    },
  });

  useEffect(() => {
    if (!data) return;

    const routines =
      workspaceAsset.routines?.filter((r) => r?.deactivated !== true) || [];
    const activeRoutineIds = new Set(routines.map(r => r.id));

    const sortedRoutines = workspaceAsset.routineState
      ?.map((state) => {
        const version = data.items.find(
          (x: { id: string }) => x.id === state.workspaceRoutineVersionId
        );
        const routine = routines?.find(
          (x) => x.id === state.workspaceRoutineId
        );
        const routineOrder = workspaceAsset.routineOrder?.find(
          (x) => x.workspaceRoutineId === state.workspaceRoutineId
        );
        
        // Include if the routine is active (either directly or embedded in an active workflow)
        const isDirectlyActive = activeRoutineIds.has(state.workspaceRoutineId);
        const isEmbeddedInActiveWorkflow = data.items.some((v: any) => 
          activeRoutineIds.has(v.workspaceRoutine?.id) && // Workflow is active
          v.config?.pages?.some((page: any) =>
            page.questions?.some((q: any) => q.workspaceRoutineId === state.workspaceRoutineId)
          )
        );
        
        return {
          state: (isDirectlyActive || isEmbeddedInActiveWorkflow) ? state : undefined,
          config: version?.config,
          order: routineOrder?.order ?? routine?.order,
          type: version?.type,
          routines,
        };
      })
      .filter((x) => x.state)
      .sort((a, b) => (a.order ?? 0) - (b?.order ?? 0));

    setSorted(sortedRoutines);
  }, [data, workspaceAsset]);

  return (
    sorted && (
      <ScrollView horizontal>
        {sorted.map((item, index) => {
          const Header =
            RoutineModules[item.type as keyof typeof RoutineModules]?.header;
          if (!Header) {
            logger?.warning(
              `Routine Module not found (${workspaceAsset.id}) ${JSON.stringify(
                item
              )}`
            );
            return null;
          }
          return (
            <View key={`view_${index}`} style={styles.header}>
              <Header
                key={`header_${index}`}
                config={item.config}
                currentState={item.state}
                routines={item.routines}
              />
            </View>
          );
        })}
      </ScrollView>
    )
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  item: {
    margin: 0,
    padding: 0,
  },
});

const GET_ROUTINE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        config
        id
        type
        workspaceRoutine {
          id
        }
      }
    }
  }
`;
